import classNames from "classnames";
import parse from "html-react-parser";
import moment from "moment";
import React, { memo } from "react";

interface ReasonCode {
  label?: string;
}

interface RoundDocument {
  id: string;
  name?: string | null;
  display_name?: string | null;
  mimetype?: string | null;
  created_at?: string | null;
  created_by?: { full_name?: string } | null;
}

interface Round {
  id: string;
  round_number: number;
  is_current?: boolean;
  state: string;
  outcome?: string | null;
  submitted_at?: string | null;
  reviewed_at?: string | null;
  review_time_days?: number | null;
  notes?: string | null;
  document_ref?: string | null;
  document_name?: string | null;
  documents?: RoundDocument[];
  responsible_documents?: RoundDocument[];
  rdd_documents?: RoundDocument[];
  reason_code?: ReasonCode | null;
}

interface Props {
  rounds: Round[];
}

const formatDate = (dateStr?: string | null) =>
  dateStr ? moment(dateStr).format("D MMM YYYY") : null;

const RoundStatusCircle = ({ round }: { round: Round }) => {
  const isCompleted = !!round.reviewed_at;
  if (isCompleted) {
    return (
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-success flex items-center justify-center text-white font-bold text-14">
        <i className="fas fa-check" />
      </div>
    );
  }
  return (
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-200 border-2 border-orange-400 flex items-center justify-center font-bold text-14 text-black-80">
      {round.round_number}
    </div>
  );
};

const OutcomePill = ({
  outcome,
  state,
}: {
  outcome?: string | null;
  state?: string | null;
}) => {
  if (outcome === "approved") {
    return (
      <span className="inline-block px-2 py-0 text-11 font-semibold rounded-full text-success-darker bg-green-200 border border-green-400">
        Approved
      </span>
    );
  }
  if (outcome === "resubmit_required") {
    return (
      <span className="inline-block px-2 py-0 text-11 font-semibold rounded-full text-error-darker bg-red-200 border border-red-400">
        Resubmit required
      </span>
    );
  }
  if (state === "awaiting_rdd_review") {
    return (
      <span className="inline-block px-2 py-0 text-11 font-semibold rounded-full text-black-80 bg-orange-200 border border-orange-400">
        Awaiting review
      </span>
    );
  }
  if (state === "awaiting_tenant_upload") {
    return (
      <span className="inline-block px-2 py-0 text-11 font-semibold rounded-full text-black-80 bg-black-10 border border-black-20">
        Awaiting upload
      </span>
    );
  }
  return null;
};

const DataCell = ({
  label,
  value,
  muted,
}: {
  label: string;
  value?: React.ReactNode;
  muted?: boolean;
}) => (
  <div className="flex flex-col">
    <span className="text-11 uppercase tracking-wide font-bold text-black-40 mb-1">
      {label}
    </span>
    <span
      className={classNames("text-14", {
        "text-black-80": !muted,
        "text-black-30": muted,
      })}
    >
      {value ?? <span className="text-black-30">—</span>}
    </span>
  </div>
);

const RoundSection = ({ round }: { round: Round }) => {
  const isCompleted = !!round.reviewed_at;
  const isResubmit = round.outcome === "resubmit_required";
  const submitted = formatDate(round.submitted_at);
  const reviewed = formatDate(round.reviewed_at);

  const stateSubtitle = isCompleted
    ? isResubmit
      ? "Reviewed and returned for resubmission"
      : "Reviewed and approved"
    : round.state === "awaiting_rdd_review"
    ? "Submitted · Awaiting RDD review"
    : "Awaiting tenant upload";

  return (
    <div className="py-4">
      <div className="flex items-start gap-3">
        <RoundStatusCircle round={round} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-16 text-black-80">
            Round {round.round_number}
            {round.is_current && (
              <span className="ml-1 font-normal text-primary">· Current</span>
            )}
          </div>
          <div className="text-12 text-primary mb-3">{stateSubtitle}</div>

          {/* 4-cell data grid */}
          <div className="grid grid-cols-4 gap-4 mb-3">
            <DataCell
              label="Submitted"
              value={submitted ?? undefined}
              muted={!submitted}
            />
            <DataCell
              label="Reviewed"
              value={reviewed ?? undefined}
              muted={!reviewed}
            />
            <DataCell
              label="Review time"
              value={
                typeof round.review_time_days === "number"
                  ? `${round.review_time_days} days`
                  : undefined
              }
              muted={typeof round.review_time_days !== "number"}
            />
            <DataCell
              label="Outcome"
              value={<OutcomePill outcome={round.outcome} state={round.state} />}
              muted={!round.outcome && round.state !== "awaiting_rdd_review" && round.state !== "awaiting_tenant_upload"}
            />
          </div>

          {/* Reason box for resubmit */}
          {isResubmit && (
            <div className="bg-orange-200 border-l-4 border-orange-400 rounded p-3 mb-3">
              <div className="text-11 uppercase tracking-wide font-bold text-black-60 mb-1">
                Reason for resubmission
              </div>
              {round.reason_code?.label && (
                <div className="text-14 font-semibold text-black-80 mb-1">
                  {round.reason_code.label}
                </div>
              )}
              {round.notes && (
                // Notes may contain mention spans (<span class="mention" data-id="...">)
                // produced by DraftText. Use html-react-parser (same as Comments tab) so
                // mention markup renders as styled elements rather than raw tag strings.
                <div className="text-14 text-black-60">{parse(round.notes)}</div>
              )}
            </div>
          )}

          {/* Documents section — prefer split responsible/rdd arrays when present;
              fall back to legacy superset `documents` for older API responses. */}
          {round.responsible_documents !== undefined || round.rdd_documents !== undefined ? (
            <div>
              {/* Responsible user documents */}
              {round.responsible_documents && round.responsible_documents.length > 0 && (
                <div className="mb-2">
                  <div className="text-11 uppercase tracking-wide font-bold text-black-40 mb-1">
                    Responsible user documents
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {round.responsible_documents.map((doc) => (
                      <a
                        key={doc.id}
                        href={`/documents/${doc.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-12 text-primary bg-black-5 border border-black-20 rounded hover:bg-black-10 w-fit self-start"
                      >
                        <i className="fas fa-file-alt text-10" />
                        <span>{doc.display_name || doc.name || "Document"}</span>
                        {doc.created_at && (
                          <span className="text-black-40 ml-1">
                            · {formatDate(doc.created_at)}
                          </span>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* RDD team documents */}
              {round.rdd_documents && round.rdd_documents.length > 0 && (
                <div className="mt-3 pt-3 border-t border-black-20">
                  <div className="text-11 uppercase tracking-wide font-bold text-black-40 mb-1">
                    RDD team documents
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {round.rdd_documents.map((doc) => (
                      <a
                        key={doc.id}
                        href={`/documents/${doc.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-12 text-primary bg-black-5 border border-black-20 rounded hover:bg-black-10 w-fit self-start"
                      >
                        <i className="fas fa-file-alt text-10" />
                        <span>{doc.display_name || doc.name || "Document"}</span>
                        {doc.created_at && (
                          <span className="text-black-40 ml-1">
                            · {formatDate(doc.created_at)}
                          </span>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state when both split arrays are present but both empty */}
              {(!round.responsible_documents || round.responsible_documents.length === 0) &&
                (!round.rdd_documents || round.rdd_documents.length === 0) && (
                <div className="text-12 text-black-40">No documents uploaded for this round.</div>
              )}
            </div>
          ) : round.documents && round.documents.length > 0 ? (
            /* Legacy: superset `documents` array (pre-split API) */
            <div className="flex flex-wrap gap-2">
              {round.documents.map((doc) => (
                <a
                  key={doc.id}
                  href={`/documents/${doc.id}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-12 text-primary bg-black-5 border border-black-20 rounded hover:bg-black-10 w-fit self-start"
                >
                  <i className="fas fa-file-alt text-10" />
                  <span>{doc.display_name || doc.name || "Document"}</span>
                  {doc.created_at && (
                    <span className="text-black-40 ml-1">
                      · {formatDate(doc.created_at)}
                    </span>
                  )}
                </a>
              ))}
            </div>
          ) : round.document_ref ? (
            /* Backward-compat fallback: if documents array is absent but a legacy
               document_ref exists, keep the old single chip. */
            <a
              href={`/documents/${round.document_ref}`}
              className="inline-flex items-center gap-1 px-2 py-1 text-12 text-primary bg-black-5 border border-black-20 rounded hover:bg-black-10 w-fit self-start"
            >
              <i className="fas fa-file-alt text-10" />
              <span>{round.document_name || "Document"}</span>
            </a>
          ) : (
            <div className="text-12 text-black-40">No documents uploaded for this round.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const SubmissionRoundHistory = ({ rounds }: Props) => {
  if (!rounds || rounds.length === 0) {
    return (
      <div className="text-14 text-black-40 py-4">No rounds yet.</div>
    );
  }

  return (
    <div>
      {rounds.map((round, idx) => (
        <React.Fragment key={round.id}>
          <RoundSection round={round} />
          {idx < rounds.length - 1 && (
            <hr className="border-dashed border-black-20 my-0" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default memo(SubmissionRoundHistory);
